
import { Author, Book, PaymentRequest, RoyaltyReport, SupportTicket, TicketMessage, FAQ } from "@/types";
import { PAYMENT_METHODS } from "./constants";

// Use consistent IDs for all mock data to simulate the database relationships
const USER_IDS = {
  admin: '11111111-1111-1111-1111-111111111111',
  author1: '22222222-2222-2222-2222-222222222222',
  author2: '33333333-3333-3333-3333-333333333333',
  author3: '44444444-4444-4444-4444-444444444444',
};

const BOOK_IDS = {
  book1: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
  book2: 'bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb',
  book3: 'cccccccc-cccc-cccc-cccc-cccccccccccc',
  book4: 'dddddddd-dddd-dddd-dddd-dddddddddddd',
  book5: 'eeeeeeee-eeee-eeee-eeee-eeeeeeeeeeee',
};

const TICKET_IDS = {
  ticket1: 'aaaaaaaa-1234-5678-90ab-cdef12345678',
  ticket2: 'bbbbbbbb-1234-5678-90ab-cdef12345678',
  ticket3: 'cccccccc-1234-5678-90ab-cdef12345678',
};

export const MOCK_AUTHORS: Author[] = [
  {
    id: USER_IDS.author1,
    name: 'John Author',
    email: 'author@example.com',
    role: 'author',
    avatar: 'https://i.pravatar.cc/150?img=1',
    books: [
      {
        id: BOOK_IDS.book1,
        title: 'The Great Novel',
        isbn: '978-3-16-148410-0',
        publishDate: '2022-01-15',
        authorId: USER_IDS.author1
      },
      {
        id: BOOK_IDS.book2,
        title: 'Sequel to Greatness',
        isbn: '978-3-16-148410-1',
        publishDate: '2023-05-20',
        authorId: USER_IDS.author1
      }
    ],
    paymentMethod: PAYMENT_METHODS.bank_transfer_us,
    paymentDetails: {
      bankName: 'Chase Bank',
      accountNumber: '************4567',
      routingNumber: '*********',
    },
    balance: 1250.75
  },
  {
    id: USER_IDS.author2,
    name: 'Jane Writer',
    email: 'jane@example.com',
    role: 'author',
    avatar: 'https://i.pravatar.cc/150?img=5',
    books: [
      {
        id: BOOK_IDS.book3,
        title: 'Poetry Collection',
        isbn: '978-3-16-148410-2',
        publishDate: '2021-11-10',
        authorId: USER_IDS.author2
      }
    ],
    paymentMethod: PAYMENT_METHODS.paypal,
    paymentDetails: {
      paypalEmail: 'jane@example.com',
    },
    balance: 430.50
  },
  {
    id: USER_IDS.author3,
    name: 'Robert Author',
    email: 'robert@example.com',
    role: 'author',
    avatar: 'https://i.pravatar.cc/150?img=8',
    books: [
      {
        id: BOOK_IDS.book4,
        title: 'Science Fiction Epic',
        isbn: '978-3-16-148410-3',
        publishDate: '2023-02-28',
        authorId: USER_IDS.author3
      },
      {
        id: BOOK_IDS.book5,
        title: 'Short Stories',
        isbn: '978-3-16-148410-4',
        publishDate: '2022-09-15',
        authorId: USER_IDS.author3
      }
    ],
    paymentMethod: PAYMENT_METHODS.check,
    paymentDetails: {
      address: '123 Author St, Booktown, BK 12345',
    },
    balance: 65.25
  }
];

export const MOCK_BOOKS: Book[] = MOCK_AUTHORS.flatMap(author => author.books);

export const MOCK_ROYALTY_REPORTS: RoyaltyReport[] = [
  {
    id: 'aaaaaaaa-1111-2222-3333-444444444444',
    quarter: 1,
    year: 2023,
    authorId: USER_IDS.author1,
    bookId: BOOK_IDS.book1,
    salesCount: 1250,
    amount: 625.50,
    currency: 'USD',
    status: 'published',
    createdAt: '2023-04-10T10:30:00Z'
  },
  {
    id: 'bbbbbbbb-1111-2222-3333-444444444444',
    quarter: 1,
    year: 2023,
    authorId: USER_IDS.author1,
    bookId: BOOK_IDS.book2,
    salesCount: 850,
    amount: 425.25,
    currency: 'USD',
    status: 'published',
    createdAt: '2023-04-10T10:35:00Z'
  },
  {
    id: 'cccccccc-1111-2222-3333-444444444444',
    quarter: 1,
    year: 2023,
    authorId: USER_IDS.author2,
    bookId: BOOK_IDS.book3,
    salesCount: 780,
    amount: 390.00,
    currency: 'USD',
    status: 'published',
    createdAt: '2023-04-10T11:30:00Z'
  },
  {
    id: 'dddddddd-1111-2222-3333-444444444444',
    quarter: 2,
    year: 2023,
    authorId: USER_IDS.author1,
    bookId: BOOK_IDS.book1,
    salesCount: 1050,
    amount: 525.00,
    currency: 'USD',
    status: 'published',
    createdAt: '2023-07-12T09:15:00Z'
  },
  {
    id: 'eeeeeeee-1111-2222-3333-444444444444',
    quarter: 2,
    year: 2023,
    authorId: USER_IDS.author1,
    bookId: BOOK_IDS.book2,
    salesCount: 920,
    amount: 460.00,
    currency: 'USD',
    status: 'published',
    createdAt: '2023-07-12T09:20:00Z'
  },
  {
    id: 'ffffffff-1111-2222-3333-444444444444',
    quarter: 2,
    year: 2023,
    authorId: USER_IDS.author3,
    bookId: BOOK_IDS.book4,
    salesCount: 130,
    amount: 65.00,
    currency: 'USD',
    status: 'published',
    createdAt: '2023-07-12T10:40:00Z'
  }
];

export const MOCK_PAYMENT_REQUESTS: PaymentRequest[] = [
  {
    id: '11111111-aaaa-bbbb-cccc-dddddddddddd',
    authorId: USER_IDS.author1,
    amount: 1000.00,
    status: 'paid',
    paymentMethod: 'bank_transfer_us',
    requestDate: '2023-05-01T14:30:00Z',
    processedDate: '2023-05-05T11:15:00Z',
    notes: 'Quarterly payment for Q1 2023'
  },
  {
    id: '22222222-aaaa-bbbb-cccc-dddddddddddd',
    authorId: USER_IDS.author2,
    amount: 390.00,
    status: 'approved',
    paymentMethod: 'paypal',
    requestDate: '2023-05-02T09:45:00Z',
    processedDate: '2023-05-03T16:20:00Z',
    notes: 'Payment for Poetry Collection royalties'
  },
  {
    id: '33333333-aaaa-bbbb-cccc-dddddddddddd',
    authorId: USER_IDS.author1,
    amount: 200.00,
    status: 'pending',
    paymentMethod: 'bank_transfer_us',
    requestDate: '2023-08-10T13:25:00Z',
    notes: 'Partial payment request for Q2 royalties'
  }
];

export const MOCK_TICKETS: SupportTicket[] = [
  {
    id: TICKET_IDS.ticket1,
    authorId: USER_IDS.author1,
    subject: 'Question about my royalty calculation',
    category: 'royalty',
    priority: 'medium',
    status: 'open',
    createdAt: '2023-08-15T08:30:00Z',
    updatedAt: '2023-08-15T08:30:00Z',
    messages: [
      {
        id: 'msg-aaaa-1111-2222-333333333333',
        ticketId: TICKET_IDS.ticket1,
        userId: USER_IDS.author1,
        userRole: 'author',
        message: 'I noticed that my Q2 royalty report has a discrepancy in the sales count. Can someone please review this?',
        createdAt: '2023-08-15T08:30:00Z'
      }
    ]
  },
  {
    id: TICKET_IDS.ticket2,
    authorId: USER_IDS.author2,
    subject: 'PayPal payment not received',
    category: 'payment',
    priority: 'high',
    status: 'in_progress',
    createdAt: '2023-08-10T14:45:00Z',
    updatedAt: '2023-08-11T09:20:00Z',
    messages: [
      {
        id: 'msg-bbbb-1111-2222-333333333333',
        ticketId: TICKET_IDS.ticket2,
        userId: USER_IDS.author2,
        userRole: 'author',
        message: 'I still haven\'t received my PayPal payment that was approved on May 3rd. Please help!',
        createdAt: '2023-08-10T14:45:00Z'
      },
      {
        id: 'msg-cccc-1111-2222-333333333333',
        ticketId: TICKET_IDS.ticket2,
        userId: USER_IDS.admin,
        userRole: 'admin',
        message: 'Thank you for bringing this to our attention. I\'m looking into it now and will get back to you shortly.',
        createdAt: '2023-08-11T09:20:00Z'
      }
    ]
  },
  {
    id: TICKET_IDS.ticket3,
    authorId: USER_IDS.author3,
    subject: 'How to update my payment method',
    category: 'account',
    priority: 'low',
    status: 'resolved',
    createdAt: '2023-07-25T11:15:00Z',
    updatedAt: '2023-07-26T13:40:00Z',
    messages: [
      {
        id: 'msg-dddd-1111-2222-333333333333',
        ticketId: TICKET_IDS.ticket3,
        userId: USER_IDS.author3,
        userRole: 'author',
        message: 'I would like to change my payment method from check to bank transfer. How can I do this?',
        createdAt: '2023-07-25T11:15:00Z'
      },
      {
        id: 'msg-eeee-1111-2222-333333333333',
        ticketId: TICKET_IDS.ticket3,
        userId: USER_IDS.admin,
        userRole: 'admin',
        message: 'You can update your payment method in your account settings under "Payment Preferences". Let me know if you need further assistance.',
        createdAt: '2023-07-25T14:30:00Z'
      },
      {
        id: 'msg-ffff-1111-2222-333333333333',
        ticketId: TICKET_IDS.ticket3,
        userId: USER_IDS.author3,
        userRole: 'author',
        message: 'I found it, thank you! I have updated my payment method successfully.',
        createdAt: '2023-07-26T10:05:00Z'
      },
      {
        id: 'msg-gggg-1111-2222-333333333333',
        ticketId: TICKET_IDS.ticket3,
        userId: USER_IDS.admin,
        userRole: 'admin',
        message: 'Great! I\'m glad you were able to update it. Your new payment method will be used for future payments.',
        createdAt: '2023-07-26T13:40:00Z'
      }
    ]
  }
];

export const MOCK_FAQS: FAQ[] = [
  {
    id: 'faq11111-aaaa-bbbb-cccc-ddddeeeeeeee',
    question: 'How are royalties calculated?',
    answer: 'Royalties are calculated based on the sales price of your book and the royalty rate specified in your publishing contract. Typically, this is a percentage of the net price (sales price minus discounts and returns) received by the publisher.',
    category: 'royalty'
  },
  {
    id: 'faq22222-aaaa-bbbb-cccc-ddddeeeeeeee',
    question: 'When are royalty payments issued?',
    answer: 'Royalty payments are processed quarterly, following the approval of royalty reports. Payment requests that meet the threshold requirements are typically processed within 5-7 business days.',
    category: 'payment'
  },
  {
    id: 'faq33333-aaaa-bbbb-cccc-ddddeeeeeeee',
    question: 'What are the payment thresholds?',
    answer: 'Payment thresholds vary by payment method: Check ($75), International Bank Transfer ($50), US Bank Transfer ($0), and PayPal ($0). You must have a balance above these thresholds to request payment via the respective method.',
    category: 'payment'
  },
  {
    id: 'faq44444-aaaa-bbbb-cccc-ddddeeeeeeee',
    question: 'How do I update my payment information?',
    answer: 'You can update your payment information by navigating to your Account Settings, then selecting "Payment Preferences". From there, you can update your preferred payment method and associated details.',
    category: 'account'
  },
  {
    id: 'faq55555-aaaa-bbbb-cccc-ddddeeeeeeee',
    question: 'What should I do if my royalty report seems incorrect?',
    answer: "If you believe there's an error in your royalty report, please submit a support ticket with specific details about the discrepancy. Our team will review your report and respond within 3 business days.",
    category: 'royalty'
  },
  {
    id: 'faq66666-aaaa-bbbb-cccc-ddddeeeeeeee',
    question: 'How long does it take to process a payment request?',
    answer: 'Once approved, payments are typically processed within 5-7 business days. The actual receipt of funds may depend on your payment method (e.g., international bank transfers may take longer).',
    category: 'payment'
  }
];

// Helper function to get all messages from tickets
export const MOCK_TICKET_MESSAGES: TicketMessage[] = MOCK_TICKETS.flatMap(ticket => ticket.messages);
